import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, Settings as SettingsIcon } from "lucide-react";
import { getSettings, updateSettings, fileUrl } from "@/lib/api";
import { invalidateSettings } from "@/lib/useSettings";
import FileUploadField from "@/components/FileUploadField";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import { toast } from "sonner";

const DEFAULTS = {
  pemerintah_header: "Pemerintah Kabupaten Seruyan",
  institution_name: "Sekretariat Dewan Perwakilan Rakyat Daerah",
  kabupaten_name: "Kabupaten Seruyan",
  alamat: "Jalan A. Yani No. 1, Kuala Pembuang, Kalimantan Tengah 74212",
  kontak: "Telepon: (0538) 21234 · Email: sekretariat.dprd@seruyankab.go.id",
  kota_ttd: "Kuala Pembuang",
  jabatan_penandatangan: "Sekretaris DPRD Kabupaten Seruyan",
  nama_penandatangan: "",
  nip_penandatangan: "",
  logo_file: null,
};

export default function SettingsPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSettings().then((d) => setForm({ ...DEFAULTS, ...d })).finally(() => setLoading(false));
  }, []);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings(form);
      invalidateSettings();
      toast.success("Pengaturan tersimpan");
    } catch { toast.error("Gagal menyimpan pengaturan"); } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-10 text-slate-500">Memuat…</div>;

  const fld = (label, name, placeholder = "") => (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <Input value={form[name] ?? ""} onChange={(e) => update(name, e.target.value)}
             placeholder={placeholder} className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
             data-testid={`settings-${name}`} />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C59B27] font-semibold">
          <SettingsIcon className="w-4 h-4" /> Pengaturan Admin
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Identitas Instansi & Kop Surat</h1>
        <p className="text-slate-600 text-sm mt-1">Perbarui logo daerah, nama instansi, alamat, dan data penandatangan yang muncul pada seluruh dokumen cetak.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">Logo Daerah</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 items-center">
            <div className="w-32 h-32 rounded-full border-2 border-[#C59B27] overflow-hidden bg-white flex items-center justify-center mx-auto md:mx-0">
              {form.logo_file ? (
                <img src={fileUrl(form.logo_file)} alt="Logo Daerah" className="w-full h-full object-contain" />
              ) : (
                <span className="text-xs text-slate-400 text-center px-2">Belum ada logo</span>
              )}
            </div>
            <FileUploadField
              label="Unggah Logo (PNG/JPG transparan direkomendasikan)"
              value={form.logo_file}
              onChange={(v) => update("logo_file", v)}
              testid="settings-logo-upload"
              accept=".png,.jpg,.jpeg,.webp"
            />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">Teks Kop Surat</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {fld("Baris 1 — Pemerintah", "pemerintah_header")}
            {fld("Baris 2 — Nama Instansi", "institution_name")}
            {fld("Baris 3 — Nama Kabupaten", "kabupaten_name")}
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Alamat Lengkap</Label>
              <Textarea value={form.alamat} onChange={(e) => update("alamat", e.target.value)}
                        className="min-h-16 bg-white border-slate-300" data-testid="settings-alamat" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Kontak (Telp/Email/Web)</Label>
              <Textarea value={form.kontak} onChange={(e) => update("kontak", e.target.value)}
                        className="min-h-16 bg-white border-slate-300" data-testid="settings-kontak" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">Penandatangan Dokumen</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {fld("Kota Tanda Tangan", "kota_ttd", "Kuala Pembuang")}
            {fld("Jabatan Penandatangan", "jabatan_penandatangan")}
            {fld("Nama Penandatangan", "nama_penandatangan")}
            {fld("NIP Penandatangan", "nip_penandatangan")}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Simpan Pengaturan
          </Button>
        </div>
      </form>

      <ChangePasswordCard />
    </div>
  );
}
